import { ReactElement, useCallback, useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useSearchParams} from "react-router-dom";
import { useToast } from "../../../../hooks/useToast";
import { HttpService } from "../../../../services/HttpService";
import { extractErrorMessage } from "../../../../helpers/extractErrorMessage";
import { getYupDynamicScheme } from "../../../../components/DocumentGeneration/helpers/yupValidationHelpers";
import Draft83bFilledModal from "../../../../components/Draft83bFilledModal/Draft83bFilledModal";
import DocumentGeneration from "../../../../components/DocumentGeneration/DocumentGeneration";
import { TDataItem } from "../../../../components/DocumentGeneration/types";
import FormWrapper15620 from "../../Form15620/FormWrapper15620";
import { TemplatesService } from "../../../PublicFile83b/TemplateService";
import {TUserCompanyConfigItem} from "../../../../@types/global";
import SelectForm83bType from "../../../../components/SelectForm83bType/SelectForm83bType";
import Loading from "../../../../components/Loading/Loading";
import { Button } from "primereact/button";



type FileCreationWrapperPropTypes = {
    companies: TUserCompanyConfigItem[];
    companyId: number;
    templateCode: string;
    templateCrypto?: string;
    paymentEnabled: boolean;
    isEveryCompany: boolean;
    templateSpouseCode?: string;
    email?: string;
    fullName?: string;
    deliveryService?: string;
    filingCreationEnabled: boolean;
    isOpenAiCompany?: boolean;
}

function FileCreationWrapper(props: FileCreationWrapperPropTypes): ReactElement {

    const {
        email = '',
        fullName = '',
        companyId,
        templateCode,
        companies,
        templateCrypto,
        paymentEnabled,
        isEveryCompany,
        templateSpouseCode,
        deliveryService='',
        isOpenAiCompany,
        filingCreationEnabled,
    } = props;

    const { show } = useToast();
    const [searchParams] = useSearchParams();

    const [fileId] = useState(() => searchParams.get('fileId'));
    const [orderNumber] = useState(() => searchParams.get('rdn'));
    const [isUpdateMode] = useState(() => searchParams.has('fileId'));
    const [isOnlyRegularFormAllowed, setOnlyRegularFormAllowed] = useState(templateCrypto === null);
    const [formData, setFormData] = useState<Record<string, any>>({});
    const [formSchema, setFormSchema] = useState<Record<string, any>>({});
    const [hasSuppose, setSuppose] = useState(false);
    const [templateId, setTemplateId] = useState('');
    const [isRegularForm, setRegularForm] = useState(true);
    const [isAlreadyFilled, setAlreadyFilled] = useState(false);
    const [alreadyFilledMessage, setAlreadyFilledMessage] = useState<string>('')
    const [isFormDataLoaded, setFormDataLoaded] = useState(false);
    const [isFormTypeSelected, setFormTypeSelected] = useState(isOnlyRegularFormAllowed);
    const [fieldsConfig, setFieldsConfig] = useState<{ configs: TDataItem[] }>({ configs: [] });
    const [isFieldsConfigLoaded, setFieldsConfigLoaded] = useState(false);
    const [docAsHtml, setDocAsHtml] = useState('');
    const [isDocAsHtmlLoaded, setDocAsHtmlLoaded] = useState(false);
    const [selectedCompany] = useState<TUserCompanyConfigItem | undefined>(() => {
        return companies.find(company => company.companyId === companyId)
    });

    const [showChooseFormType, setShowChooseFormType] = useState((!isUpdateMode && !orderNumber && !isOnlyRegularFormAllowed));

    const { data: deliveryPrices } = useQuery({
        queryKey: TemplatesService.fetchInitialInfo.queryKey,
        queryFn: () => TemplatesService.fetchInitialInfo.queryFn(),
        enabled:true
    });
    const {price, uspsPrice} = deliveryPrices || {price:0, uspsPrice:0};
    const setSupposeHandler = useCallback((hasSuppose: boolean) => {

        setSuppose(hasSuppose);
        setTemplateId(() => {
            const templateId = hasSuppose ? templateSpouseCode : templateCode;
            return templateId as string;
        });

    }, [templateCode, templateSpouseCode]);

    const fetchFieldsConfig = useCallback(() => {
        HttpService.get(
            `/api/companies/${companyId}/file83b/getPlaceholdersByTemplateCodeDb/${templateId}${isUpdateMode ? `/edit` : ''}`
        ).then(({ data }) => {
   
            setFieldsConfigLoaded(true);
            setFieldsConfig(data);
            setFormSchema(data.configs ? getYupDynamicScheme(data.configs) : {});
        }).catch((error) => {
            extractErrorMessage({
                error: error,
                show: show,
            });
        })
    }, [companyId, templateId, isUpdateMode]);

    const fetchDocAsHtml = useCallback(() => {
        HttpService.get(
            `/api/companies/${companyId}/file83b/exportDocAsHtml/${templateId}`
        ).then(({ data }) => {
            setDocAsHtml(data);
            setDocAsHtmlLoaded(true);
        }).catch((error) => {
            extractErrorMessage({
                error: error,
                show: show,
            });
        })
    }, [companyId, templateId]);

    useEffect(() => {
        if (templateId !== '') {
            fetchFieldsConfig();
            fetchDocAsHtml();
        }
    }, [templateId, fetchFieldsConfig]);

    useEffect(() => {
        if (isUpdateMode) {
            HttpService.get(
                `/api/companies/${companyId}/file83b/${fileId}`
            ).then(({ data }) => {
                setFormData({
                    ...data,
                    ...(data.allowManualRoundUp ? {
                        roundingMode:"NO_ROUNDING"
                    } : {})
                });
                setFormDataLoaded(true);
                setRegularForm(data?.regular);
                setFormTypeSelected(true);
                setSupposeHandler(data?.jointlyWithSpouse);
            }).catch((error) => {
                if (error?.response?.status === 400) {
                    setAlreadyFilled(true);
                    if(Object.keys(error?.response?.data?.fieldErrors || {}).length > 0) {
                        const fieldErrors = error?.response?.data?.fieldErrors;
                        setAlreadyFilledMessage(String(Object.values(fieldErrors)[0]));
                    } else {
                        setAlreadyFilledMessage(error?.response?.data?.detail);
                    }
                } else {
                    extractErrorMessage({
                        error: error,
                        show: show,
                    });
                }
            })
        }
    }, [templateSpouseCode]);


    
    if (!filingCreationEnabled && !isUpdateMode) {
        return (
            <div className="flex align-items-center justify-content-center min-h-screen bg-gray-50">
                <div className="surface-card p-5 shadow-2 border-round text-center">
                    <i className="pi pi-lock text-4xl text-red-500 mb-3"></i>
                    <h2 className="text-2xl mb-2">Access Denied</h2>
                    <p className="text-gray-600 mb-4">
                        You do not have permission to view this page.
                    </p>
                    <Button label="Go Back" icon="pi pi-arrow-left" onClick={() => window.history.back()} />
                </div>
            </div>
        );
    }

    return (
        <div className="h-full max-w-full">
            {
                isAlreadyFilled ? (
                    <Draft83bFilledModal
                        message={alreadyFilledMessage}
                        onClose={() => {
                            setAlreadyFilled(false);
                            setOnlyRegularFormAllowed(false);
                            setFormTypeSelected(false);
                            setRegularForm(false);
                            setShowChooseFormType(true);
                        }}
                        filingCreationEnabled={filingCreationEnabled} />
                ) : null
            }
            {
                isFormTypeSelected ? (
                    isRegularForm ? (
                        <DocumentGeneration
                            selectedCompany={selectedCompany}
                            companyId={companyId}
                            email={email}
                            fullName={fullName}
                            isSupposeExists={hasSuppose}
                            setSuppose={setSupposeHandler}
                            template={templateId}
                            data={fieldsConfig}
                            isPublic={false}
                            price={price}
                            uspsPrice={uspsPrice}   
                            requiredFieldsLoading={!isFieldsConfigLoaded}
                            docAsHtml={docAsHtml}
                            docAsHtmlLoading={!isDocAsHtmlLoaded}
                            preFilledFormValues={formData}
                            file83bUpdate={isUpdateMode}
                            preFilledFormValuesLoading={!isFormDataLoaded}
                            fileId={fileId || ''}
                            showSelectTemplate={Boolean(templateSpouseCode)}
                            signAfterEmbeddedPaymentOrderNumber={orderNumber}
                            paymentEnabled={(paymentEnabled && !formData?.paid)}
                            formSchema={formSchema}
                            isEveryCompany={isEveryCompany}
                            isOpenAiCompany={isOpenAiCompany}
                            showDeliveryService={(deliveryService === 'NONE' && paymentEnabled && !formData?.paid)}
                        />
                    ) : (
                        <FormWrapper15620
                            isPublic={false}
                            email={email}
                            price={price}
                            uspsPrice={uspsPrice} 
                            deliveryService={deliveryService}
                            isDataLoaded={isUpdateMode ? isFormDataLoaded : true}
                            companyId={companyId}
                            templateId={templateCrypto}
                            isPublicOpenAi={false}
                            formData={formData}
                            paymentEnabled={(paymentEnabled && !formData?.paid)} />
                    )
                ) : (
                    showChooseFormType ? (
                        <SelectForm83bType isPublic={false}
                            onSelectHandler={setRegularForm}
                            selectedForm={isRegularForm}
                            setFormTypeSelected={setFormTypeSelected} />
                    ) : (
                        <div className="w-full h-full flex align-items-center justify-content-between">
                            <Loading />
                        </div>
                    )
                )
            }

        </div>
    );
}

export default FileCreationWrapper;